import React, { useEffect, useRef, useState, useMemo } from 'react'
import { Marker, Popup, Tooltip, Polyline, TileLayer } from 'react-leaflet'
import { Icon, DivIcon } from 'leaflet'
import moment from 'moment/moment'
import { Button, Checkbox, Icon as AntIcon, Tooltip as AntTooltip } from 'antd'
import { BaseMap } from '../../components/Map'
import MapPlaybackControl from './MapPlaybackControl'
import MapTooltipContent from './MapTooltipContent'
import AlertNotificationStack from './AlertNotificationStack'
import { buildFaultyHdopRouteSegments, buildOverspeedRouteSegments } from './overspeedRouteUtils'
import { buildGreenDrivingMapEvents } from './greenDrivingEventUtils'
import { CATEGORY_COLORS, CATEGORY_ICONS, CATEGORY_LABELS } from '../../util/drawingUtils'
import { TILE_LAYERS, getLeafletTileConfig, getTileLayerAttribution } from '../../util/mapTiles'
import axios from 'util/Api'
import './style.css'

// Strips 0x prefix — mirrors backend normalizeScannedId
const normalizeScannedId = (id) => `${id || ''}`.trim().replace(/^0x/i, '')

// Returns true for a non-zero hex string (mirrors backend isRealHexScannedId)
const isValidScannedId = (id) => {
  const v = normalizeScannedId(id)
  return v.length > 0 && v.length % 2 === 0 && /^[0-9a-fA-F]+$/.test(v) && !/^0+$/.test(v)
}

// ── Beacon detection dot helpers ────────────────────────────────────────────────
const BEACON_STATUS_COLORS = {
  FOUND: '#52c41a',
  LOST: '#f5222d',
  ACTIVE: '#1890ff',
  TOWING: '#fa8c16',
  DISCONNECTED: '#8c8c8c',
}

function makeBeaconStatusDotIcon(status) {
  const color = BEACON_STATUS_COLORS[(status || '').toUpperCase()] || '#722ed1'
  return new DivIcon({
    className: '',
    html: `<div style="width:7px;height:7px;border-radius:50%;background:${color};border:1.5px solid rgba(255,255,255,0.85);box-shadow:0 0 2px rgba(0,0,0,0.35)"></div>`,
    iconSize: [7, 7],
    iconAnchor: [3, 3],
  })
}

function parseBeaconPos(r) {
  if (r.lat_long && typeof r.lat_long === 'string') {
    const p = r.lat_long.split(',').map(Number)
    if (p.length === 2 && !isNaN(p[0]) && !isNaN(p[1]) && p[0] !== 0 && p[1] !== 0) return p
  }
  if (r.latlng && typeof r.latlng === 'string') {
    const p = r.latlng.split(',').map(Number)
    if (p.length === 2 && !isNaN(p[0]) && !isNaN(p[1]) && p[0] !== 0 && p[1] !== 0) return p
  }
  const lat = parseFloat(r.latitude || r.lat || '')
  const lng = parseFloat(r.longitude || r.lng || r.lon || '')
  if (!isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0) return [lat, lng]
  return null
}

const ALL_POI_CATEGORIES = Object.keys(CATEGORY_LABELS);

// ── Towing helpers ─────────────────────────────────────────────────────────────


const greenDrivingIconUrls = {
  1: require('assets/images/icon/acceleration.png'),
  2: require('assets/images/icon/brake.png'),
  3: require('assets/images/icon/turning.png')
}

const buildGreenDrivingDivIcon = (iconUrl) => new DivIcon({
  className: 'green-driving-chip-marker',
  html: `
    <div class="gd-chip-wrap">
      <img src="${iconUrl}" alt="" class="gd-chip-icon" />
    </div>
  `,
  iconSize: [28, 28],
  iconAnchor: [14, 14]
})

const greenDrivingIcons = {
  1: buildGreenDrivingDivIcon(greenDrivingIconUrls[1]),
  2: buildGreenDrivingDivIcon(greenDrivingIconUrls[2]),
  3: buildGreenDrivingDivIcon(greenDrivingIconUrls[3])
}

// Animated Marker Component for smooth movement
// For each beacon, find the most recent towEvent at or before currentTs
function getTowingStateFromEvents(towEvents, currentTs) {
  if (!towEvents || !towEvents.length) return {}
  const byBeacon = {}
  for (const ev of towEvents) {
    if (ev.synthetic) continue
    const ts = Number(ev.eventTs)
    if (ts > currentTs) continue
    if (!byBeacon[ev.instanceId] || ts > Number(byBeacon[ev.instanceId].eventTs)) {
      byBeacon[ev.instanceId] = ev
    }
  }
  return byBeacon // { [instanceId]: latestEventRecord }
}

function AnimatedMarker({ position, angle, asset, timestamp, isMoving, manpower, speed }) {
  const markerRef = useRef(null)
  const prevPositionRef = useRef(position)
  const animationRef = useRef(null)

  // Smoothly animate marker to new position
  useEffect(() => {
    if (!markerRef.current || !markerRef.current.leafletElement) return

    const marker = markerRef.current.leafletElement
    const prevPos = prevPositionRef.current
    const newPos = position

    // If position changed and vehicle is moving, animate smoothly
    if (isMoving && (prevPos[0] !== newPos[0] || prevPos[1] !== newPos[1])) {
      const startLat = parseFloat(prevPos[0])
      const startLng = parseFloat(prevPos[1])
      const endLat = parseFloat(newPos[0])
      const endLng = parseFloat(newPos[1])

      const duration = 9000 // 9 seconds animation (data comes every 10 seconds)
      const startTime = Date.now()

      // Cancel any existing animation
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }

      const animate = () => {
        const elapsed = Date.now() - startTime
        const progress = Math.min(elapsed / duration, 1)

        // Ease-out function for smoother deceleration
        const easeProgress = 1 - Math.pow(1 - progress, 3)

        const currentLat = startLat + (endLat - startLat) * easeProgress
        const currentLng = startLng + (endLng - startLng) * easeProgress

        marker.setLatLng([currentLat, currentLng])

        if (progress < 1) {
          animationRef.current = requestAnimationFrame(animate)
        }
      }

      animationRef.current = requestAnimationFrame(animate)
    } else {
      // Not moving or same position, just set directly
      marker.setLatLng(newPos)
    }

    prevPositionRef.current = newPos

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
    }
  }, [position, isMoving])

  // Create rotated icon for moving vehicle
  const movingIcon = useMemo(() => {
    const rotation = angle || 0
    return new DivIcon({
      className: 'animated-marker-icon',
      html: `
        <div class="moving-marker-container" style="transform: rotate(${rotation}deg);">
          <img src="${require('assets/images/icon/navigation_moving.png')}" 
               alt="moving" 
               style="width: 32px; height: 32px;" />
        </div>
      `,
      iconSize: [32, 32],
      iconAnchor: [16, 16]
    })
  }, [angle])

  return (
    <Marker
      ref={markerRef}
      position={position}
      icon={movingIcon}
    >
      <Popup>{moment(timestamp).toLocaleString()}</Popup>
      <Tooltip direction="bottom" offset={[0, 20]} opacity={1} permanent>
        <div style={{ textAlign: 'center', lineHeight: 1.4 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, flexWrap: 'wrap' }}>
            <h4 style={{ margin: 0 }}>
              {[asset && asset['assetCode'], asset && asset['ownerCode']].filter(Boolean).join(' / ') || '-'}
            </h4>
            {speed != null && speed !== '' && (
              <span style={{ fontWeight: 400, fontSize: 11, color: '#555' }}>{speed} km/h</span>
            )}
          </div>
          {manpower && manpower.name && (
            <div style={{ fontSize: 11, marginTop: 5, paddingTop: 4, borderTop: '1px solid rgba(0,0,0,0.12)' }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#1890ff', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 2 }}>Operator</div>
              <div style={{ fontWeight: 600 }}>{manpower.name}</div>
              {manpower.mobileNo && <div style={{ opacity: 0.75 }}>{manpower.mobileNo}</div>}
            </div>
          )}
        </div>
      </Tooltip>
    </Marker>
  )
}
// Helper function to validate lat/long coordinates
const isValidLatLong = (latLong) => {
  if (!latLong || typeof latLong !== 'string') return false
  const coords = latLong.split(',')
  if (coords.length !== 2) return false
  const lat = parseFloat(coords[0])
  const lng = parseFloat(coords[1])
  // console.log('lat =>', lat, 'lng =>', lng)
  return lat !== 0 && lng !== 0 && !isNaN(lat) && !isNaN(lng)
}

// Helper function to get valid lat/long from data array
const getValidLatLong = (data, currentIndex) => {
  if (!data || !data.length) return null

  // First try to find a valid previous record
  for (let i = currentIndex - 1; i >= 0; i--) {
    if (data[i] && data[i].lat_long && isValidLatLong(data[i].lat_long)) {
      return {
        latLong: data[i].lat_long,
        isInvalid: true,
        originalIndex: currentIndex
      }
    }
  }

  // If no previous valid record, try to find next valid record
  for (let i = currentIndex + 1; i < data.length; i++) {
    if (data[i] && data[i].lat_long && isValidLatLong(data[i].lat_long)) {
      return {
        latLong: data[i].lat_long,
        isInvalid: true,
        originalIndex: currentIndex
      }
    }
  }

  return null
}

const formatRangeTime = (ts) => {
  if (!ts) return '-'
  return moment(ts).format('DD-MM-YYYY HH:mm:ss')
}

function DeviceMap({ asset = {}, deviceData = {}, rangeData = [], showRangeData, mapSpeed = 1, source = 'second', IsSSEActive = false, isMaximized = false, onToggleMaximize, towingData = null, bleAssetsMap = {} }) {
  const [currentLocation, setCurrentLocation] = useState([0, 0]);
  const [showFleetRangeData, setShowFleetRangeData] = useState(false)
  const [index, setIndex] = useState(0);
  const [polylines, setPolylines] = useState([]);
  const [fleetRangeData, setFleetRangeData] = useState([]);
  const [fleetTimeRange, setFleetTimeRange] = useState([0, 0])
  const [playbackSpeed, setPlaybackSpeed] = useState(0.5)
  const [isPlaying, setIsPlaying] = useState(true)
  const [marker, setMarker] = useState(null)
  const [indexInterval, setIndexInterval] = useState(null)
  const [lineColor, setLineColor] = useState('blue')
  const [zoom, setZoom] = useState(source === 'public' ? 15 : 18)
  const [deviceStatus, setDeviceStatus] = useState('offline')
  const [showOverspeedRouteOverlay, setShowOverspeedRouteOverlay] = useState(false)
  const [showFaultyHdopRouteOverlay, setShowFaultyHdopRouteOverlay] = useState(false)
  const [showGreenDrivingOverlay, setShowGreenDrivingOverlay] = useState(false)
  const [hiddenPoiCategories, setHiddenPoiCategories] = useState([])
  const [poiPanelOpen, setPoiPanelOpen] = useState(false)
  const [loadedPoiCategories, setLoadedPoiCategories] = useState([])
  const [tileLayerType, setTileLayerType] = useState('current')
  const [scrollWheelZoom, setScrollWheelZoom] = useState(false)
  const [isDarkMode, setIsDarkMode] = useState(
    typeof document !== 'undefined' && document.body
      ? document.body.classList.contains('dark-theme')
      : false
  )

  // Manpower cache: scanned_id → manpower object (null = fetched but not found)
  const manpowerCacheRef = useRef({})
  const manpowerFetchingRef = useRef(new Set())

  // Keep isDarkMode in sync with body class changes
  useEffect(() => {
    if (typeof document === 'undefined' || !document.body) return
    const observer = new MutationObserver(() => {
      setIsDarkMode(document.body.classList.contains('dark-theme'))
    })
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] })
    return () => observer.disconnect()
  }, [])

  // Resolved tile URL — reacts to both tileLayerType and dark-mode switches
  const resolvedTileUrl = useMemo(() => {
    if (tileLayerType !== 'current') return TILE_LAYERS[tileLayerType].url || undefined
    const cfg = getLeafletTileConfig(isDarkMode)
    return cfg.url || undefined
  }, [tileLayerType, isDarkMode])

  const resolvedTileSubdomains = useMemo(() => {
    if (tileLayerType !== 'current') return TILE_LAYERS[tileLayerType].subdomains || undefined
    const cfg = getLeafletTileConfig(isDarkMode)
    return cfg.subdomains || undefined
  }, [tileLayerType, isDarkMode])

  const prevIndexRef = useRef(0)
  const prevFleetDataRef = useRef(null)
  const notifTimerRef = useRef(null)
  const [towingNotification, setTowingNotification] = useState(null)


  const map1Ref = useRef()

  // Handle zoom change
  const handleZoomChange = () => {
    if (map1Ref.current && map1Ref.current.leafletElement) {
      setZoom(map1Ref.current.leafletElement.getZoom());
    }
  };

  useEffect(() => {
    setZoom(source === 'public' ? 15 : 18)
  }, [source])

  // Tell Leaflet to recalculate tile coverage after the container resizes (e.g. maximize/restore)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (map1Ref.current && map1Ref.current.leafletElement) {
        map1Ref.current.leafletElement.invalidateSize();
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [isMaximized])

  useEffect(() => {
    // console.log('Show .... ', showRangeData)
    if (!showRangeData) {
      setShowFleetRangeData(false)
      setPolylines([])
      setFleetRangeData([])
      setFleetTimeRange([0, 0])
      setIndex(0)
      setShowOverspeedRouteOverlay(false)
      setShowFaultyHdopRouteOverlay(false)
      setShowGreenDrivingOverlay(false)
    }
  }, [showRangeData]);

  useEffect(() => {
    // mapSpeed=0 => slow (0.5x), mapSpeed=1 => normal (0.5x default), mapSpeed=2 => fast (2x)
    const nextSpeed = mapSpeed === 2 ? 2 : 0.5
    setPlaybackSpeed(nextSpeed)
  }, [mapSpeed])

  useEffect(() => {
    // console.log('deviceData =>', deviceData);
    // Simulate getting new marker location, you can replace this with actual logic
    if (deviceData && deviceData.lat_long && map1Ref.current && map1Ref.current.leafletElement) {
      // console.log(Object.entries(deviceData)[0][1]['lat_long'])
      const newLoc = deviceData.lat_long;
      if (newLoc && newLoc.split(',').length === 2) {
        const newMarkerLocation = [parseFloat(newLoc.split(',')[0]), parseFloat(newLoc.split(',')[1])]; // New marker location
        const isInitialLoad = currentLocation[0] === 0 && currentLocation[1] === 0;
        setCurrentLocation(newMarkerLocation);

        setTimeout(() => {
          if (map1Ref.current && map1Ref.current.leafletElement) {
            if (isInitialLoad) {
              // First load - use flyTo for initial positioning
              map1Ref.current.leafletElement.flyTo(newMarkerLocation, source === 'public' ? 15 : 18);
            } else {
              // Subsequent updates - use panTo to smoothly move without zoom animation
              map1Ref.current.leafletElement.panTo(newMarkerLocation, { animate: true, duration: 0.5 });
            }
          }
        }, 500);
      }
    }
  }, [deviceData]);


  useEffect(() => {
    if (deviceData && JSON.stringify(deviceData) !== '{}') {
      const { movement, ignition, timestamp, incoming_time } = deviceData || {};

      const mv = movement && Number(movement) && !!Number(movement)
      const ign = ignition && Number(ignition) && !!Number(ignition)

      const timeDifference = moment(new Date().getTime()).diff(moment(timestamp), 'seconds');
      // console.log('tt ', timeDifference);
      const isDeviceOffline = !(timeDifference && timeDifference <= 300);

      let isSynching = false;
      if (isDeviceOffline && incoming_time) {
        const tts = moment(new Date().getTime()).diff(moment(incoming_time), 'seconds');
        // console.log(tts);
        isSynching = (tts && tts <= 300);
      }
      // console.log(isSynching, isDeviceOffline);

      const status = isSynching ? 'syncing' : isDeviceOffline ? 'offline' : (ign && mv) ? 'play' : (ign && !mv) ? 'pause' : 'stop';
      setDeviceStatus(status);

      setMarker(new Icon({
        iconUrl: require(`assets/images/icon/${status}.png`),
        iconSize: [25, 25],
      }))

    }
  }, [deviceData]);

  useEffect(() => {
    // console.log(showFleetRangeData)
    // setShowFleetRangeData(showRangeData)
    if (!showFleetRangeData) {
      if (indexInterval) clearInterval(indexInterval)
      setPolylines([])
      setIndex(0)

      if (deviceData && deviceData.lat_long && map1Ref.current && map1Ref.current.leafletElement) {
        const newLoc = deviceData.lat_long;
        if (newLoc && newLoc.split(',').length === 2) {
          const newMarkerLocation = [newLoc.split(',')[0], newLoc.split(',')[1]]; // New marker location
          setCurrentLocation(newMarkerLocation);
        }
      }
    }
  }, [showFleetRangeData]);

  // useEffect(() => {
  //   // console.log(asset)
  //   // if (asset && asset.assetModel && asset.assetModel.icon)
  //   if (asset && deviceData) {
  //     //
  //     // icon = require(`assets/images/icon/stop.png`); // 'https://janus-av-assets.s3.ap-south-1.amazonaws.com/assets/stop.png'; //deviceMaps.has(dId) && deviceMaps.get(dId)['assetModel'] && deviceMaps.get(dId)['assetModel']['icon']
  //     // const ignition = deviceData.ignition && !!Number(deviceData.ignition);
  //     // const movement = deviceData.movement && !!Number(deviceData.movement);
  //     // if (ignition && movement)
  //     //     icon = 'https://janus-av-assets.s3.ap-south-1.amazonaws.com/assets/play.png'
  //     // if (ignition && !movement)
  //     //     icon = 'https://janus-av-assets.s3.ap-south-1.amazonaws.com/assets/pause.png'
  //
  //     setMarker(new Icon({
  //       iconUrl: require(`assets/images/icon/${deviceStatus}.png`),  //asset.assetModel.icon,
  //       iconSize: [25, 25],
  //       // shadowSize:   [50, 64], // size of the shadow
  //       // iconAnchor:   [22, 94], // point of the icon which will correspond to marker's location
  //       // shadowAnchor: [4, 62],  // the same for the shadow
  //       // popupAnchor:  [-3, -76] // point from which the popup should open relative to the iconAnchor
  //     }))
  //   }
  // }, [asset, deviceData]);

  const filteredFleetRangeData = useMemo(() => {
    if (!fleetRangeData.length) return []
    const startIndex = Math.max(0, Math.min(fleetTimeRange[0], fleetRangeData.length - 1))
    const endIndex = Math.max(0, Math.min(fleetTimeRange[1], fleetRangeData.length - 1))
    const fromIndex = Math.min(startIndex, endIndex)
    const toIndex = Math.max(startIndex, endIndex)
    return fleetRangeData.slice(fromIndex, toIndex + 1)
  }, [fleetRangeData, fleetTimeRange])

  const overspeedEvents = useMemo(() => {
    const overspeedSummary = rangeData && rangeData.processedData && rangeData.processedData.overspeed_summary
    return (overspeedSummary && overspeedSummary.overspeed_events) || []
  }, [rangeData])

  const unauthorizedZoneEvents = useMemo(() => {
    const summary = rangeData && rangeData.processedData && rangeData.processedData.unauthorized_zone_summary
    return (summary && summary.unauthorized_zone_events) || []
  }, [rangeData])

  const overspeedRouteSegments = useMemo(() => {
    if (!showFleetRangeData) return []
    if (!filteredFleetRangeData || !filteredFleetRangeData.length) return []
    if (!overspeedEvents || !overspeedEvents.length) return []
    return buildOverspeedRouteSegments(filteredFleetRangeData, overspeedEvents)
  }, [showFleetRangeData, filteredFleetRangeData, overspeedEvents])

  const faultyHdopEvents = useMemo(() => {
    const faultyHdopSummary = rangeData && rangeData.processedData &&
      (rangeData.processedData.faulty_hdop_summary || rangeData.processedData.faulty_hdop_summery)
    return (faultyHdopSummary && faultyHdopSummary.faulty_hdop_events) || []
  }, [rangeData])

  const faultyHdopRouteSegments = useMemo(() => {
    if (!showFleetRangeData) return []
    if (!filteredFleetRangeData || !filteredFleetRangeData.length) return []
    if (!faultyHdopEvents || !faultyHdopEvents.length) return []
    return buildFaultyHdopRouteSegments(filteredFleetRangeData, faultyHdopEvents)
  }, [showFleetRangeData, filteredFleetRangeData, faultyHdopEvents])

  const greenDrivingEvents = useMemo(() => {
    const greenDrivingSummary = rangeData && rangeData.processedData &&
      (rangeData.processedData.green_driving_summary || rangeData.processedData.green_drivng_summery)
    return buildGreenDrivingMapEvents(greenDrivingSummary)
  }, [rangeData])

  // Towing state at the current animation frame (used by the bottom-left panel)

  // All beacon detection positions for the fetched period — static layer rendered over the route
  // Show only beacon records detected within the last 30s of the current animation frame
  const beaconDetectionPoints = useMemo(() => {
    if (!showFleetRangeData || !towingData || !towingData.data || !towingData.data.length || !filteredFleetRangeData.length) return []
    const currentData = filteredFleetRangeData[index]
    if (!currentData) return []
    const currentTs = Number(currentData.timestamp)
    const WINDOW_MS = 30 * 1000
    return towingData.data
      .filter(r => {
        const ts = Number(r.timestamp)
        return ts <= currentTs && ts >= currentTs - WINDOW_MS
      })
      .map((r, i) => {
        const pos = parseBeaconPos(r)
        if (!pos) return null
        const mac = r.instanceId || r.instanceid || r.instancsid || r.mac || ''
        const status = (r.status || '').toUpperCase()
        return { pos, mac, status, rssi: r.rssi || r.RSSI || r.rssiValue, ts: r.timestamp, key: `bdet-${mac}-${r.timestamp}` }
      }).filter(Boolean)
  }, [index, towingData, filteredFleetRangeData, showFleetRangeData])

  // Derived towing state at current animation frame from towEvents
  const currentTowingState = useMemo(() => {
    if (!showFleetRangeData || !towingData || !towingData.towEvents || !towingData.towEvents.length || !filteredFleetRangeData.length) return {}
    const currentData = filteredFleetRangeData[index]
    if (!currentData) return {}
    return getTowingStateFromEvents(towingData.towEvents, Number(currentData.timestamp))
  }, [index, towingData, showFleetRangeData, filteredFleetRangeData])

  // Beacons whose latest event status is TOWING at current frame
  const currentTowingBeacons = useMemo(() =>
    Object.values(currentTowingState).filter(ev => ev.status === 'TOWING')
    , [currentTowingState])

  useEffect(() => {
    if (showFleetRangeData && towingData && towingData.towEvents && towingData.towEvents.length && filteredFleetRangeData.length) {
      const currentData = filteredFleetRangeData[index]
      const prevData = filteredFleetRangeData[Math.max(0, prevIndexRef.current)]
      if (currentData && prevData) {
        const currentTs = Number(currentData.timestamp)
        const prevTs = Number(prevData.timestamp)
        const fired = towingData.towEvents.filter(ev => {
          const ts = Number(ev.eventTs)
          return ts > prevTs && ts <= currentTs
        })
        // if (fired.length > 0) {
        //   const ev = fired[0]
        //   const id = ev.instanceId || ''
        //   const text = ev.status === 'TOWING'        ? `${id} confirmed TOWING`
        //              : ev.status === 'DISCONNECTED'  ? `${id} disconnected`
        //              : `${id} → ${ev.status}`
        //   setTowingNotification({ type: ev.status, text })
        //   if (notifTimerRef.current) clearTimeout(notifTimerRef.current)
        //   notifTimerRef.current = setTimeout(() => setTowingNotification(null), 3500)
        // }
      }
    }
    prevIndexRef.current = index
  }, [index])


  const assetOrganisationId = useMemo(() => {
    const org = asset && asset.organisation
    if (!org) return null
    if (typeof org === 'string') return org
    return org._id || org.id || null
  }, [asset])

  useEffect(() => {
    if (indexInterval) clearInterval(indexInterval)
    if (!filteredFleetRangeData.length) return

    // Always init on new data regardless of play state — only the interval is gated behind isPlaying.
    // Without this split, loading data while paused never calls setShowFleetRangeData(true).
    const isNewData = prevFleetDataRef.current !== filteredFleetRangeData
    prevFleetDataRef.current = filteredFleetRangeData

    if (isNewData) {
      setPolylines([])
      setIndex(0)
      setPolylines(filteredFleetRangeData.map((d, idx) => {
        if (isValidLatLong(d.lat_long)) {
          return { position: [d.lat_long.split(',')[0], d.lat_long.split(',')[1]], isInvalid: false }
        }
        const validData = getValidLatLong(filteredFleetRangeData, idx)
        if (validData) {
          return { position: [validData.latLong.split(',')[0], validData.latLong.split(',')[1]], isInvalid: true }
        }
        return { position: [0, 0], isInvalid: true }
      }))
      setShowFleetRangeData(true)
    }

    if (!isPlaying) return

    const intervalId = setInterval(() => {
      setIndex(prevIndex => (prevIndex + 1) % filteredFleetRangeData.length)
    }, Math.max(20, Math.round(100 / (playbackSpeed || 1))))

    setIndexInterval(intervalId)
    return () => clearInterval(intervalId)
  }, [filteredFleetRangeData, playbackSpeed, isPlaying])

  useEffect(() => {
    // console.log('polylines =>', polylines)
    if (polylines && polylines.length) {
      const firstPosition = polylines[0].position
      // console.log('firstPosition =>', firstPosition)
      setCurrentLocation(firstPosition || [0, 0])
    }
  }, [polylines])

  useEffect(() => {
    return () => {
      setIndex(0)
      indexInterval && clearInterval(indexInterval)
    };
  }, []);

  useEffect(() => {
    // console.log('rangeData =>=>', rangeData)
    if (rangeData && rangeData.data && rangeData.data.length) {
      const sortedData = [...rangeData.data].sort((a, b) => {
        const ta = Number(a && a.timestamp) || 0
        const tb = Number(b && b.timestamp) || 0
        return ta - tb
      })
      setFleetRangeData(sortedData)
      setFleetTimeRange([0, Math.max(sortedData.length - 1, 0)])
      setIndex(0)
      setIsPlaying(true)
      setShowOverspeedRouteOverlay(false)
      setShowFaultyHdopRouteOverlay(false)
      setShowGreenDrivingOverlay(false)
      const defaultZoom = source === 'public' ? 15 : 18
      const targetZoom = defaultZoom - 3
      setZoom(targetZoom)
      const firstValid = sortedData.find(d => d.lat_long && isValidLatLong(d.lat_long))
      if (firstValid && map1Ref.current && map1Ref.current.leafletElement) {
        const parts = firstValid.lat_long.split(',').map(Number)
        if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
          setCurrentLocation([parts[0], parts[1]])
          map1Ref.current.leafletElement.setView([parts[0], parts[1]], targetZoom)
        }
      }
    }
  }, [rangeData && rangeData.data]);

  // Pre-fetch manpower for every unique valid scanned_id in the loaded fleet history
  useEffect(() => {
    if (!fleetRangeData || !fleetRangeData.length) return
    const toFetch = {}
    for (const pt of fleetRangeData) {
      const raw = pt.scanned_id
      if (!raw || !isValidScannedId(raw)) continue
      const sid = normalizeScannedId(raw)  // strip 0x so backend hex-reversal logic works
      if (sid in manpowerCacheRef.current || manpowerFetchingRef.current.has(sid)) continue
      if (!toFetch[sid]) toFetch[sid] = pt.timestamp  // use first occurrence timestamp
    }
    const ids = Object.keys(toFetch)
    console.log('[Manpower] fleet loaded — valid scanned_ids to fetch:', ids)
    if (!ids.length) return
    ids.forEach(sid => manpowerFetchingRef.current.add(sid))
    Promise.all(
      ids.map(sid =>
        axios.get(`manpower/rfid-details?rfid=${encodeURIComponent(sid)}&timestamp=${toFetch[sid]}`)
          .then(res => {
            const mp = (res && res.data && res.data.manpower) || null
            manpowerCacheRef.current[sid] = mp
            console.log(`[Manpower] ${sid} →`, mp ? `${mp.name} / ${mp.mobileNo}` : 'not found')
          })
          .catch(err => {
            manpowerCacheRef.current[sid] = null
            console.warn(`[Manpower] fetch failed for ${sid}:`, err && err.message)
          })
          .finally(() => { manpowerFetchingRef.current.delete(sid) })
      )
    )
  }, [fleetRangeData])

  const togglePoiCategory = (cat) => {
    setHiddenPoiCategories((prev) =>
      prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat]
    );
  };

  const currentAnimationTs = Number(((filteredFleetRangeData || [])[index] || {}).timestamp || 0)

  const handleAlertClick = (alert) => {
    setIsPlaying(false)
    const targetTs = alert.startTs - 2 * 60 * 1000
    let bestIdx = 0
    for (let i = 0; i < filteredFleetRangeData.length; i++) {
      const ts = Number((filteredFleetRangeData[i] || {}).timestamp || 0)
      if (ts >= targetTs) { bestIdx = i; break }
      if (i === filteredFleetRangeData.length - 1) bestIdx = i
    }
    setIndex(bestIdx)
  }

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      {/* ── Alert notification stack (bottom-left, pre-loaded, activates with animation) ── */}
      <AlertNotificationStack
        visible={showFleetRangeData}
        currentTs={currentAnimationTs}
        overspeedEvents={overspeedEvents}
        unauthorizedZoneEvents={unauthorizedZoneEvents}
        towingEvents={towingData && towingData.towEvents}
        bleAssetsMap={bleAssetsMap}
        onAlertClick={handleAlertClick}
      />

      {/* ── Towing overlay panel ── */}
      {/* ── Towing event toast ── */}
      {showFleetRangeData && towingNotification && (
        <div className={`device-map-towing-notif device-map-towing-notif--${(towingNotification.type || '').toLowerCase()}`}>
          {towingNotification.text}
        </div>
      )}

      {/* ── Floating POI category toggle panel (only shown when POI data exists) ── */}
      {loadedPoiCategories.length > 0 && (
        <div className="device-map-poi-toggle">
          <button
            className="device-map-poi-toggle-btn"
            onClick={() => setPoiPanelOpen((v) => !v)}
            title="Toggle POI categories"
          >
            📍 POI Layers {poiPanelOpen ? '▲' : '▼'}
          </button>
          {poiPanelOpen && (
            <div className="device-map-poi-panel">
              {loadedPoiCategories.map((cat) => {
                const hidden = hiddenPoiCategories.includes(cat);
                return (
                  <AntTooltip key={cat} title={CATEGORY_LABELS[cat]} placement="left">
                    <button
                      className={`device-map-poi-cat-btn${hidden ? ' device-map-poi-cat-btn-off' : ''}`}
                      onClick={() => togglePoiCategory(cat)}
                      style={{ borderLeftColor: hidden ? '#d9d9d9' : CATEGORY_COLORS[cat] }}
                    >
                      <span className="device-map-poi-cat-icon">{CATEGORY_ICONS[cat]}</span>
                      <span className="device-map-poi-cat-label">{CATEGORY_LABELS[cat]}</span>
                      {hidden
                        ? <span className="device-map-poi-cat-eye">👁‍🗨</span>
                        : <span className="device-map-poi-cat-eye" style={{ color: '#52c41a' }}>👁</span>
                      }
                    </button>
                  </AntTooltip>
                );
              })}
            </div>
          )}
        </div>
      )}


      <div
        className={`map-container map-container--${source}`}
        style={{ height: '100%' }}
        onClick={() => setScrollWheelZoom(true)}
        onMouseLeave={() => setScrollWheelZoom(false)}
      >
        {/* ── Maximize / restore button ── */}
        {typeof onToggleMaximize === 'function' && (
          <button
            className="device-map-maximize-btn"
            onClick={onToggleMaximize}
            title={isMaximized ? 'Restore map' : 'Maximize map'}
          >
            <AntIcon type={isMaximized ? 'fullscreen-exit' : 'fullscreen'} />
          </button>
        )}

        {/* ── Floating tile layer switcher ── */}
        <div className="device-map-tile-switcher">
          {Object.keys(TILE_LAYERS).map((key) => (
            <button
              key={key}
              className={`device-map-tile-btn${tileLayerType === key ? ' device-map-tile-btn-active' : ''}`}
              onClick={() => setTileLayerType(key)}
              title={TILE_LAYERS[key].label}
            >
              {TILE_LAYERS[key].label}
            </button>
          ))}
        </div>

        <BaseMap
          mapRef={map1Ref}
          center={currentLocation}
          zoom={zoom}
          scrollWheelZoom={scrollWheelZoom}
          attributionControl={true}
          onZoomEnd={handleZoomChange}
          tileLayerUrl={resolvedTileUrl}
          tileLayerMaxZoom={TILE_LAYERS[tileLayerType].maxZoom || (source === 'public' ? 18 : 22)}
          tileLayerSubdomains={resolvedTileSubdomains}
          tileLayerAttribution={getTileLayerAttribution(tileLayerType)}
          geoJsonLabelsZoom={zoom}
          geoJsonLabelsMinZoom={source === 'public' ? 10 : 17}
          geoFenceOverlayOrganisationId={assetOrganisationId}
          geoFenceOverlayAsset={asset}
          geoFenceOverlayInteractive={false}
          geoFenceOverlayLabelMinZoom={source === 'public' ? 10 : 12}
          geoFenceOverlayHiddenCategories={hiddenPoiCategories}
          geoFenceOverlayOnCategoriesLoaded={setLoadedPoiCategories}
        >
          {/* Hybrid: street label overlay on top of satellite */}
          {TILE_LAYERS[tileLayerType] && TILE_LAYERS[tileLayerType].overlay && (
            <TileLayer
              url={TILE_LAYERS[tileLayerType].overlay}
              subdomains={TILE_LAYERS[tileLayerType].overlaySubdomains || ['a', 'b', 'c']}
              opacity={TILE_LAYERS[tileLayerType].overlayOpacity || 0.4}
              maxZoom={22}
              attribution={TILE_LAYERS[tileLayerType].overlayAttribution}
            />
          )}

          {showFleetRangeData && polylines && polylines.length &&
            <Polyline pathOptions={{ color: 'blue' }}
              color={'blue'} positions={polylines.map(p => p.position)} />
          }
          {showFleetRangeData && showOverspeedRouteOverlay && overspeedRouteSegments && overspeedRouteSegments.length > 0 &&
            overspeedRouteSegments.map((segment) => (
              <Polyline
                key={segment.key}
                positions={segment.positions}
                color="#ff1f1f"
                opacity={0.55}
                weight={6}
                lineCap="round"
                lineJoin="round"
              >
                <Tooltip direction="top" opacity={1} sticky>
                  <div style={{ minWidth: 120 }}>
                    <div><strong>Overspeed Event</strong></div>
                    <div>Max: {segment.maxSpeed != null ? Number(segment.maxSpeed).toFixed(2) : '-'} km/h</div>
                    <div>Avg: {segment.avgSpeed != null ? Number(segment.avgSpeed).toFixed(2) : '-'} km/h</div>
                    <div>Duration: {segment.durationMs != null ? (Number(segment.durationMs) / 1000).toFixed(2) : '-'} s</div>
                  </div>
                </Tooltip>
              </Polyline>
            ))
          }
          {showFleetRangeData && showFaultyHdopRouteOverlay && faultyHdopRouteSegments && faultyHdopRouteSegments.length > 0 &&
            faultyHdopRouteSegments.map((segment) => (
              <Polyline
                key={segment.key}
                positions={segment.positions}
                color="#f97316"
                opacity={0.55}
                weight={6}
                lineCap="round"
                lineJoin="round"
              />
            ))
          }
          {showFleetRangeData && showGreenDrivingOverlay && greenDrivingEvents && greenDrivingEvents.length > 0 &&
            greenDrivingEvents.map((event) => (
              <Marker
                key={event.key}
                position={event.position}
                icon={greenDrivingIcons[event.eventTypeKey] || greenDrivingIcons[1]}
              >
                <Tooltip direction="top" opacity={1} sticky>
                  <div style={{ minWidth: 140 }}>
                    <div><strong>{event.eventName || 'Green Driving Event'}</strong></div>
                    <div>{event.timestamp != null ? moment(event.timestamp).format('DD-MM-YYYY HH:mm:ss') : '-'}</div>
                  </div>
                </Tooltip>
              </Marker>
            ))
          }
          {/* Beacon detection points — small status-coloured dots over the route */}
          {beaconDetectionPoints.map(({ pos, mac, status, rssi, ts, key }) => {
            const bleAsset = bleAssetsMap && mac ? bleAssetsMap[mac] : null
            const assetLabel = bleAsset
              ? [bleAsset.assetCode, bleAsset.ownerCode].filter(Boolean).join(' / ') || null
              : null
            return (
              <Marker key={key} position={pos} icon={makeBeaconStatusDotIcon(status)} zIndexOffset={-50}>
                <Tooltip direction="top" offset={[0, -5]} opacity={0.95}>
                  <div style={{ fontSize: 11, lineHeight: 1.5 }}>
                    {assetLabel && <div style={{ fontWeight: 700, marginBottom: 1 }}>{assetLabel}</div>}
                    {mac && <div style={{ fontFamily: 'monospace', fontWeight: 600, opacity: assetLabel ? 0.6 : 1, fontSize: assetLabel ? 10 : 11 }}>{mac}</div>}
                    {status && <div style={{ color: BEACON_STATUS_COLORS[status] || undefined }}>{status}</div>}
                    {rssi && <div>{rssi} dBm</div>}
                    {ts && <div style={{ opacity: 0.7 }}>{moment(Number(ts)).format('HH:mm:ss')}</div>}
                  </div>
                </Tooltip>
              </Marker>
            )
          })}

          {showFleetRangeData && filteredFleetRangeData && filteredFleetRangeData.length && filteredFleetRangeData[index] && filteredFleetRangeData[index].lat_long &&
            (() => {
              const currentData = filteredFleetRangeData[index]
              let position, isInvalid = false

              if (isValidLatLong(currentData.lat_long)) {
                position = [currentData.lat_long.split(',')[0], currentData.lat_long.split(',')[1]]
                isInvalid = false
              } else {
                // Try to get valid lat/long from previous or next records
                const validData = getValidLatLong(filteredFleetRangeData, index)
                if (validData) {
                  position = [validData.latLong.split(',')[0], validData.latLong.split(',')[1]]
                  isInvalid = true
                } else {
                  position = [0, 0]
                  isInvalid = true
                }
              }

              return (
                <Marker
                  position={position}
                  icon={new Icon({
                    iconUrl: isInvalid
                      ? "https://janus-av-assets.s3.ap-south-1.amazonaws.com/resources/pointer-black.png"
                      : "https://janus-av-assets.s3.ap-south-1.amazonaws.com/assets/pointer.png",
                    iconSize: [35, 35],
                    iconAnchor: [35 / 2, 35 + 9],
                    // shadowSize:   [50, 64], // size of the shadow
                    // iconAnchor:   [22, 94], // point of the icon which will correspond to marker's location
                    // shadowAnchor: [4, 62],  // the same for the shadow
                    // popupAnchor:  [-3, -76] // point from which the popup should open relative to the iconAnchor
                  })}>

                  <Tooltip direction="top" opacity={1}
                    permanent offset={[0, -50]}>
                    <MapTooltipContent
                      currentData={currentData}
                      index={index}
                      filteredFleetRangeData={filteredFleetRangeData}
                      towingData={towingData}
                      manpowerForPoint={(() => {
                        const sid = currentData.scanned_id && isValidScannedId(currentData.scanned_id)
                          ? normalizeScannedId(currentData.scanned_id) : null
                        return sid ? (manpowerCacheRef.current[sid] || null) : null
                      })()}
                      bleAssetsMap={bleAssetsMap}
                      asset={asset}
                      isInvalid={isInvalid}
                      overspeedEvents={overspeedEvents}
                      unauthorizedZoneEvents={unauthorizedZoneEvents}
                    />
                  </Tooltip>
                  {/*<Popup>{moment(filteredFleetRangeData[index].timestamp).format('DD-MM-YYYY HH:mm:ssA')}</Popup>*/}
                </Marker>
              )
            })()
          }

          {(marker && deviceData && deviceData.lat_long && deviceStatus === 'play' && (IsSSEActive || source === 'public')) ?
            <AnimatedMarker
              position={[deviceData.lat_long.split(',')[0], deviceData.lat_long.split(',')[1]]}
              angle={deviceData.ang || 0}
              asset={asset}
              timestamp={deviceData.timestamp}
              isMoving={true}
              manpower={deviceData.manpower || null}
              speed={deviceData.speed_oem != null ? deviceData.speed_oem : deviceData.speed_gnss}
            />
            : (marker && deviceData && deviceData.lat_long &&
              <Marker position={[deviceData.lat_long.split(',')[0], deviceData.lat_long.split(',')[1]]}
                icon={marker}>
                <Popup>{moment(deviceData.timestamp).toLocaleString()}</Popup>
                <Tooltip direction="bottom" offset={[0, 20]} opacity={1} permanent>
                  <h4 style={{ margin: 0 }}>
                    {[asset && asset['assetCode'], asset && asset['ownerCode']].filter(Boolean).join(' / ') || '-'}
                  </h4>
                </Tooltip>
              </Marker>
            )
          }

        </BaseMap>
      </div>
      <MapPlaybackControl
        visible={showFleetRangeData && showRangeData && !!fleetRangeData && fleetRangeData.length > 1}
        fleetRangeData={fleetRangeData}
        fleetTimeRange={fleetTimeRange}
        onTimeRangeChange={setFleetTimeRange}
        filteredFleetRangeData={filteredFleetRangeData}
        currentIndex={index}
        formatRangeTime={formatRangeTime}
        playbackSpeed={playbackSpeed}
        onSpeedChange={setPlaybackSpeed}
        isPlaying={isPlaying}
        onPlayPause={() => setIsPlaying((p) => !p)}
        onReplay={() => { setIndex(0); setIsPlaying(false); }}
        onStepBack={() => { setIsPlaying(false); setIndex(prev => Math.max(0, prev - 1)); }}
        onStep={() => { setIsPlaying(false); setIndex(prev => Math.min(prev + 1, filteredFleetRangeData.length - 1)); }}
      />
      <div className="map-route-controls">
        {showFleetRangeData && polylines && polylines.length &&
          <Button size={'small'} type={'default'} onClick={() => {
            setShowFleetRangeData(false)
            setPolylines([])
            setFleetRangeData([])
            setFleetTimeRange([0, 0])
            setIndex(0)
            setShowOverspeedRouteOverlay(false)
            setShowFaultyHdopRouteOverlay(false)
            setShowGreenDrivingOverlay(false)
          }}>Clear History Route</Button>
        }
        {showFleetRangeData && (
          <div className="map-route-toggle-group">
            <Checkbox
              checked={showOverspeedRouteOverlay}
              disabled={!overspeedRouteSegments || !overspeedRouteSegments.length}
              onChange={(event) => setShowOverspeedRouteOverlay(!!(event && event.target && event.target.checked))}
            >
              Overspeed Route
            </Checkbox>
            <Checkbox
              checked={showFaultyHdopRouteOverlay}
              disabled={!faultyHdopRouteSegments || !faultyHdopRouteSegments.length}
              onChange={(event) => setShowFaultyHdopRouteOverlay(!!(event && event.target && event.target.checked))}
            >
              Weak GPS Route
            </Checkbox>
            <Checkbox
              checked={showGreenDrivingOverlay}
              disabled={!greenDrivingEvents || !greenDrivingEvents.length}
              onChange={(event) => setShowGreenDrivingOverlay(!!(event && event.target && event.target.checked))}
            >
              Green Driving Route
            </Checkbox>
          </div>
        )}
      </div>
    </div>
  );
}

export default DeviceMap;
